import shaka from 'shaka-player';
import initShakaPlayerMux from '@mux/mux-data-shakaplayer';
import debounce from 'lodash.debounce';
import {
  SURROUND_AUDIO_SUFFIX,
  TEXT_TRACK_DEFAULT,
  TEXT_TRACK_MIDDLE,
  TEXT_TRACK_MINI_PLAYER,
  SUBTITLES_OFF,
} from '../../../common/constants';
import Logger from '../../utils/logger';
import PlayerBase from '../player_base';
import request, { requestCertificate } from '../../utils/request';
import { ErrorCategories, ShakaError } from '../../utils/errors';
import platform, { BROWSER_TYPES, IS_SAFARI, OS_TYPES } from '../../utils/platform';
import CustomTextDisplay from './custom_text_display';
import {
  extractContentIdFromInitData,
  getNormalizedBuffer,
  filterForUniqueLanguages,
  normalizeTracks,
  pickTrackByQuality,
  sortTracksByBandwidthAsc,
  b64EncodeUnicode,
  getMuxMetadata,
  getStreamType,
} from './utils';
import Mp4TtmlParser from './ttml-parser';
import VttParser from './vtt-parser';
import SafariSubtitlesManager from './safari_subtitles_manager';
import { domClasses } from '../../dom';
import { getLanguage, findTracksByLang } from '../../utils/utils';

// Safari version where we don't need the Safari subtitles manager
const SAFARI_VERSION_THRESHOLD = 17;

// see https://magine.atlassian.net/browse/MDM-9627
const IS_AFFECTED_FF = platform.browser === BROWSER_TYPES.FIREFOX && platform.majorVersion >= 67;

// video player on Safari automagically manages video quality, and we can't change that
// so it has no options except 'auto'
const QUALITIES = IS_SAFARI ? ['auto'] : ['auto', 'low', 'medium', 'high'];

// delay between firing manifestwillupdate event
const MANIFEST_DELAY = 4000;

shaka.polyfill.installAll();

// Use legacy Apple Media Keys for Safari
if (IS_SAFARI) {
  shaka.polyfill.PatchedMediaKeysApple.install();
}

// Use custom mp4 TTML parser due to https://github.com/tvoli/hipster-player/issues/381
shaka.text.TextEngine.registerParser('application/mp4; codecs="stpp"', () => new Mp4TtmlParser());
shaka.text.TextEngine.registerParser('application/mp4; codecs="stpp.TTML.im1t"', () => new Mp4TtmlParser());

// Use custom VTT parser due to https://magine.atlassian.net/browse/MDM-11574
shaka.text.TextEngine.registerParser('text/vtt', () => new VttParser());
shaka.text.TextEngine.registerParser('text/vtt; codecs="vtt"', () => new VttParser());

export default class ShakaPlayer extends PlayerBase {
  constructor(parent) {
    super(parent);

    if (IS_SAFARI) {
      this._safariSubtitlesManager = new SafariSubtitlesManager(this);
    }
  }

  videoPlayer = null;
  _liveOffsetInterval = null;
  _playbackStarted = false;
  _startOfLiveWindow = 0;
  _manifestInterval;
  _creditsStartSent = false;
  _closingCreditsStart = 0;
  _seekOffset = 0;

  async probeSupport() {
    const browser = platform.browser;

    if (!shaka.Player.isBrowserSupported()) {
      const error = new Error('DRM error - unsupported browser');
      error.detail = {
        code: 605,
        module: 'shaka',
        category: ErrorCategories.drm,
      };

      this.parent._errorhandlingService?.onDRMError(error);
      return;
    }

    const drmConfig = {
      [BROWSER_TYPES.CHROME]: { drm: 'widevine', protocol: 'dash' },
      [BROWSER_TYPES.EDGE]: { drm: 'widevine', protocol: 'dash' },
      [BROWSER_TYPES.FIREFOX]: { drm: 'widevine', protocol: 'dash' },
      [BROWSER_TYPES.OPERA]: { drm: 'widevine', protocol: 'dash' },
      [BROWSER_TYPES.SAFARI]: { drm: 'fairplay', protocol: 'hls' },
      [BROWSER_TYPES.IE]: { drm: 'playready', protocol: 'dash' },
      [BROWSER_TYPES.EDGE_LEGACY]: { drm: 'playready', protocol: 'dash' },
      CYPRESS: { drm: 'clearkey', protocol: 'dash' },
      MOBILE: { drm: 'clearkey', protocol: 'dash' },
      DEFAULT: { drm: 'widevine', protocol: 'dash' },
    };

    // Check if cypress and then return cypress config
    if (window.Cypress || navigator.userAgent.includes('Cypress')) {
      this.parent.model = this.parent.model.merge(drmConfig.CYPRESS);
      return;
    }

    if (platform.os === OS_TYPES.IOS || platform.os === OS_TYPES.ANDROID) {
      this.parent.model = this.parent.model.merge(drmConfig.MOBILE);
      return;
    }

    if (drmConfig[browser]) {
      this.parent.model = this.parent.model.merge(drmConfig[browser]);
      return;
    }
    // fallback to default DRM hoping it will work anyways
    this.parent.model = this.parent.model.merge(drmConfig.DEFAULT);
  }

  performSeek = debounce(() => {
    this.seek(this.parent.model.currentTime + this._seekOffset);
    this._seekOffset = 0;
  }, 500);

  fastForward(jump = 15) {
    const {
      isPlayingDaiAds,
      rights,
    } = this.parent.model;

    if ((isPlayingDaiAds && !rights.adsFastForward)
      || (!isPlayingDaiAds && !rights.fastForward)) {
      return;
    }

    this._seekOffset += jump;

    this.performSeek();
  }

  rewind(jump = 15) {
    const {
      isPlayingDaiAds,
      rights,
    } = this.parent.model;

    if ((isPlayingDaiAds && !rights.adsRewind)
      || (!isPlayingDaiAds && !rights.rewind)) {
      return;
    }

    this._seekOffset -= jump;

    this.performSeek();
  }

  _applyFFWorkaround = false;

  seek(time) {
    if (!this.videoPlayer) {
      return;
    }

    const timeSeek = Number(time);
    const { currentTime } = this.parent.model;

    if (IS_AFFECTED_FF && timeSeek < currentTime) {
      this._applyFFWorkaround = true;

      const activeTrack = this.videoPlayer.getVariantTracks()
        .find(track => track.active === true);

      const lowQualityTrack = this.videoPlayer.getVariantTracks()
        .filter(track => (activeTrack ? track.language === activeTrack.language : true))
        .sort(sortTracksByBandwidthAsc)[0];

      // disable adaptation cause we need to switch tracks manually
      // would be restored by the changeBitratePreference() call
      this._disableAbr(true);
      this.videoPlayer.selectVariantTrack(lowQualityTrack, true);
    }

    this.parent.playerElement.currentTime = this._absoluteTimeToPlayerTime(timeSeek);
    Logger.debug('Seeking to', timeSeek);
  }

  async play() {
    const {
      isLive,
      rights,
      isPlayedToEnd,
    } = this.parent.model;

    // make sure browser starts playback from the live point if needed
    if (isLive && !rights.pause) {
      if (IS_SAFARI) {
        // reload the asset in Safari is the safest way to catchup the live point
        await this.load(this.parent.model.playlist);
      } else {
        this.seek(Date.now() / 1000);
      }
    }

    // apply FF workaround if starting playback from the very beginning
    if (IS_AFFECTED_FF && isPlayedToEnd) {
      this.seek(0);
    }

    await this.parent.playerElement.play()
      .catch((e) => {
        Logger.warn('Failed to play:', e);
      });
  }

  pause() {
    if (!this.parent.model.isPlayingDaiAds || this.parent.model.rights.adsPause) {
      this.parent.playerElement.pause();
    }
  }

  invokeAirplay() {
    this.parent.playerElement.webkitShowPlaybackTargetPicker();
  }

  async togglePlay() {
    if (this.parent.model.isPlaying) {
      this.pause();
    } else {
      await this.play();
    }
  }

  toggleMute() {
    if (this.parent.model.volume === 0) {
      this.changeVolume(this.parent._getActiveVideoVolume());
    } else {
      this.changeVolume(0);
    }
  }

  changeVolume(volume, saveToStorage = true) {
    this.parent.playerElement.volume = volume;
    this.parent._saveVolume(volume, saveToStorage);
  }

  changeAudioPreference(selectedAudio) {
    if (!this.videoPlayer) {
      return;
    }

    const selectedAudioLang = getLanguage(selectedAudio); // en-6CH -> en
    const tracks = this.videoPlayer.getVariantTracks();
    const activeTrack = tracks.find(track => track.active);

    let langTracks = findTracksByLang(tracks, selectedAudioLang, this.parent.model.surroundAudio);

    if (!langTracks.length) {
      langTracks = findTracksByLang(tracks, selectedAudioLang, !this.parent.model.surroundAudio);
    }
    langTracks.sort(sortTracksByBandwidthAsc);

    if (!langTracks.length) {
      Logger.info(`changeAudioPreference: can't find ${selectedAudio} audio tracks`);

      if (activeTrack) {
        Logger.info(`changeAudioPreference: falling back to ${activeTrack.language}`);
        this.parent.model = this.parent.model.merge({
          selectedAudio: activeTrack.language,
        });
      }

      return;
    }

    // pick a track with the closest bandwidth
    const newTrack = langTracks.reduce((bestMatchTrack, track) => {
      if (!bestMatchTrack) {
        return track;
      }

      const lowestDifferenceInBandwidth = Math.abs(bestMatchTrack.bandwidth - activeTrack.bandwidth);
      const differenceInBandwidth = Math.abs(track.bandwidth - activeTrack.bandwidth);

      return differenceInBandwidth < lowestDifferenceInBandwidth ? track : bestMatchTrack;
    }, null);

    Logger.info(`changeAudioPreference: changing audio to ${selectedAudio}`);

    this.parent.model = this.parent.model.merge({ selectedAudio: newTrack.language });

    this._disableAbr(true);
    this.videoPlayer.selectVariantTrack(newTrack, true, 2); // speed up track switch
    this._disableAbr(false);
  }

  changeTextTrackPreference(text) {
    Logger.log('changeTextTrackPreference', 'Wanting to change to', text);

    this._getActiveTextTrack()?.removeEventListener('cuechange', this._onCueChange);

    const selectedTextTrack = this._getAndPrepareTextTracks()
      .find(textTrack => textTrack.language === text);

    if (!selectedTextTrack) {
      this.parent.model = this.parent.model.merge({ selectedText: SUBTITLES_OFF });
      this.videoPlayer.setTextTrackVisibility(false);
      Logger.log('changeTextTrackPreference', 'hiding text');
      return;
    }

    if (this.parent.model.selectedText === text) {
      if (!this.videoPlayer.isTextTrackVisible()) {
        this.videoPlayer.setTextTrackVisibility(true);
      }

      Logger.log('changeTextTrackPreference', 'ignoring', selectedTextTrack);
      return;
    }

    Logger.log('changeTextTrackPreference', 'Changing text to', selectedTextTrack.language);
    this.parent.model = this.parent.model.merge({ selectedText: selectedTextTrack.language });
    this.videoPlayer.selectTextTrack(selectedTextTrack);
    if (!this.videoPlayer.isTextTrackVisible()) {
      this.videoPlayer.setTextTrackVisibility(true);
    }

    this._getActiveTextTrack()?.addEventListener('cuechange', this._onCueChange);

    this._safariSubtitlesManager?.clearLoadedTextTrackSegments();
  }

  _onCueChange = () => {
    const playerElement = this.parent.playerElement;
    const selectedTextTrack = Array.from(playerElement.textTracks).find(tt => tt.mode === 'showing');
    if (selectedTextTrack) {
      const wrapperElement = this.parent.wrapperElement;
      const subtitles = this.parent.subtitlesElement;
      const isMiniPlayer = wrapperElement.classList.contains(domClasses.miniPlayer);
      const hasControl = subtitles.classList.contains(domClasses.withControls);

      let position = TEXT_TRACK_DEFAULT;
      if (isMiniPlayer) {
        position = TEXT_TRACK_MINI_PLAYER;
      } else if (hasControl) {
        position = TEXT_TRACK_MIDDLE;
      }

      const { activeCues, cues } = selectedTextTrack;
      const uniqueCuesArr = [];

      // update cue position and remove any duplicate cue from activeCues list
      for (let i = 0; i < activeCues?.length; i += 1) {
        const cue = activeCues[i];
        const exists = uniqueCuesArr.find(c => c.text === cue.text && c.startTime === cue.startTime && c.endTime === cue.endTime);
        if (cue.line !== position || exists) {
          // when control is shown and cue position is changed, if we seek back,
          // shaka will add cue with original position back to activeCues list.
          // we need to remove cue with same text and start/end time to avoid showing duplicate subtitle
          selectedTextTrack.removeCue(cue);
        }
        if (cue.line !== position && !exists) {
          cue.line = position;
          selectedTextTrack.addCue(cue);
        }
        if (!exists) {
          uniqueCuesArr.push(cue);
        }
      }

      // update cue position in cues list
      for (let i = 0; i < cues?.length; i += 1) {
        const cue = cues[i];
        if (cue.line !== position) {
          selectedTextTrack.removeCue(cue);
          cue.line = position;
          selectedTextTrack.addCue(cue);
        }
      }
    }
  };

  // this is to change subtitle position on safari
  changeTextTrackPosition(position) {
    if (this.model.textTracksPosition === position) return;

    const selectedTextTrack = this._getActiveTextTrack();
    if (selectedTextTrack && this.videoPlayer?.isTextTrackVisible()) {
      this._onCueChange();

      this.model = this.model.merge({
        textTracksPosition: position,
      });
    }
  }
  _hideSubtitles = () => {
    const { selectedText } = this.parent.model;
    if (selectedText && selectedText !== SUBTITLES_OFF) {
      this.videoPlayer.setTextTrackVisibility(false);
    }
  };

  _showSubtitles = () => {
    const { selectedText } = this.parent.model;
    if (selectedText && selectedText !== SUBTITLES_OFF) {
      this.videoPlayer.setTextTrackVisibility(true);
    }
  };

  changeBitratePreference(selectedBitrate) {
    if (!this.videoPlayer) {
      return;
    }

    if (!QUALITIES.includes(selectedBitrate)) {
      throw new Error(`unknown quality level: ${selectedBitrate}`);
    }

    const enableAdaptation = selectedBitrate === 'auto';
    this.videoPlayer.configure({
      abr: {
        enabled: enableAdaptation,
      },
    });

    if (enableAdaptation) {
      Logger.info('Setting adaptation to', selectedBitrate);
      this.parent.model = this.parent.model.merge({
        selectedBitrate,
      });
      return;
    }

    const variantTracks = this._getAndPrepareVideoTracks();
    if (!variantTracks) {
      Logger.warn('changeBitratePreference: there are no tracks available');
      return;
    }

    const selectedVariantTrack = pickTrackByQuality(variantTracks, selectedBitrate);

    this.videoPlayer.selectVariantTrack(selectedVariantTrack);
    this.parent.model = this.parent.model.merge({
      selectedBitrate,
    });
    Logger.info('Setting adaptation to', selectedBitrate);
    // Update our model with data regarding the selected track,
    // so we can use this data when sending data to Telemetry

    const videoObject = {
      videoBandwidth: selectedVariantTrack.videoBandwidth,
      audioBandwidth: selectedVariantTrack.audioBandwidth,
      videoCodec: selectedVariantTrack.videoCodec,
      audioCodec: selectedVariantTrack.audioCodec,
    };
    this._updateModelWithBitrateAndCodecInformation(videoObject);
  }

  async _prepareManifest(manifestUri) {
    if (!IS_SAFARI) {
      return {};
    }

    const resp = await request({
      url: manifestUri,
      authorize: false,
      type: 'GET',
    });

    const manifestLines = resp.split('\n');
    if (!manifestLines.some(line => line.startsWith('#EXT-X-MEDIA')
      && (line.includes('6ch-ac3') || line.includes('CHANNELS="6"')))) {
      return {};
    }

    const basePath = manifestUri.split('/')
      .slice(0, -1)
      .join('/');

    let changeNextStreamLine = false;
    const updatedManifest = manifestLines
      .map((line) => {
        let changedLine = line;
        if (
          line.startsWith('#EXT-X-MEDIA')
          && (line.includes('6ch-ac3') || line.includes('CHANNELS="6"'))
        ) {
          changedLine = changedLine.replace(/LANGUAGE="([a-z]{2,3})"/, `LANGUAGE="$1${SURROUND_AUDIO_SUFFIX}"`);
        }

        if (line.startsWith('#EXT-X-MEDIA')) {
          changedLine = changedLine.replace(/URI="(.+)"/, `URI="${basePath}/$1"`);
          changedLine = changedLine.replace('AUTOSELECT=YES,', '');
        }

        if (changeNextStreamLine) {
          changeNextStreamLine = false;
          changedLine = `${basePath}/${changedLine}`;
        }

        if (line.startsWith('#EXT-X-STREAM-INF')) {
          changeNextStreamLine = true;
        }

        return changedLine;
      })
      .join('\n');

    const mimeType = 'application/x-mpegurl';
    const manifest = `data:${mimeType};base64,${b64EncodeUnicode(updatedManifest)}`;

    return {
      mimeType,
      manifest,
    };
  }

  async load(manifestUri, playbackStartTime) {
    this.parent.model = this.parent.model.merge({
      playbackStartTime,
    });

    if (this.parent.model.isChromeCasting) {
      this.unload();
      throw new Error('player is chromecasting');
    }

    if (!this.videoPlayer) {
      await this._setupVideoPlayer();
    } else {
      this._changeVideo();
    }

    clearInterval(this._liveOffsetInterval);
    this._liveOffsetInterval = setInterval(this._updateOffsetFromLive, 1000);

    // Let player decide when to start when not specified
    const startTime = playbackStartTime
      ? this._absoluteTimeToPlayerTime(playbackStartTime)
      : null;

    this._playbackStarted = false;
    this._creditsStartSent = false;
    this._closingCreditsStart = 0;
    this._startOfLiveWindow = 0;
    this._presentationStartTime = 0;

    const {
      manifest = manifestUri,
      mimeType,
    } = await this._prepareManifest(manifestUri);

    return this.videoPlayer
      .load(manifest, startTime, mimeType)
      .then(() => {
        if (this !== this.parent.currentPlayer) {
          return;
        }

        this._configureVideoPlayer();

        if (IS_SAFARI && this.parent.playerElement.hasAttribute('disableRemotePlayback')) {
          // sometimes shaka player will add disableRemotePlayback attribute which will disable airplay ui from displaying
          // remove it so that airplay button is shown
          this.parent.playerElement.removeAttribute('disableRemotePlayback');
        }
        // eslint-disable-next-line consistent-return
        return this.parent.playerElement.play()
          .then(() => {
            if (IS_SAFARI) {
              this.changeAudioPreference(this.parent.model.preferredAudio);
              this.changeTextTrackPreference(this.parent.model.preferredText);
            }
          })
          .catch((e) => {
            Logger.warn('Failed to play:', e);
          });
      })
      .catch((e) => {
        // we can safely ignore the LOAD_INTERRUPTED errors
        // they are thrown when there were sequential .load() calls
        // but shaka-player can deal with them
        if (e.code === shaka.util.Error.Code.LOAD_INTERRUPTED) {
          return;
        }

        throw e;
      });
  }

  _changeVideo() {
    const {
      model,
    } = this.parent;

    this.videoPlayer.mux?.emit('videochange', getMuxMetadata(model, model.viewable));
  }

  async _setupVideoPlayer() {
    const {
      model: {
        drm,
        license,
        certificate,
        headers,
        authorizationKey,
        accessToken,
        viewable,
        hipsterPlayerVersion,
        muxKey,
        userId,
      },
    } = this.parent;

    if (window.WebKitPlaybackTargetAvailabilityEvent) {
      this.parent.playerElement.addEventListener('webkitplaybacktargetavailabilitychanged', this._onAirplayAvailabilityChange);
      this.parent.playerElement.addEventListener(
        'webkitcurrentplaybacktargetiswirelesschanged',
        this._onAirplayDestinationChange,
      );
    }
    this.parent.playerElement.addEventListener('canplay', this._setPlaybackStartTimeOnCanPlayEvent);
    this.parent.playerElement.addEventListener('timeupdate', this._onTimeupdate);
    this.parent.playerElement.addEventListener('ended', this._onEnded);
    this.parent.playerElement.addEventListener('playing', this._onPlaying);
    this.parent.playerElement.addEventListener('pause', this._onPause);
    this.parent.playerElement.addEventListener('seeking', this._onSeeking);
    this.parent.playerElement.addEventListener('seeked', this._onSeeked);
    this.parent.playerElement.addEventListener('volumechange', this._onVolumeChange);
    this.parent.playerElement.addEventListener('loadedmetadata', this._onLoadedmetadata);
    this.parent.addEventListener('adsActivated', this._hideSubtitles);
    this.parent.addEventListener('adsDeactivated', this._showSubtitles);

    if (
      IS_SAFARI && certificate
      && platform.majorVersion < SAFARI_VERSION_THRESHOLD
      && (viewable?.playable || viewable?.defaultPlayable || {}).kind !== 'CHANNEL'
    ) {
      this._safariSubtitlesManager?.setup();
    }

    this.videoPlayer = new shaka.Player(this.parent.playerElement);

    // MUX
    if (muxKey) {
      initShakaPlayerMux(this.videoPlayer, {
        data: {
          env_key: muxKey,
          player_name: 'Hipster Player',
          player_version: hipsterPlayerVersion,
          viewer_user_id: userId,
          ...getMuxMetadata(this.parent.model, viewable),
        },
      });
    }

    this.videoPlayer.addEventListener('error', this._onError);
    this.videoPlayer.addEventListener('adaptation', this._onAdaptation);
    this.videoPlayer.addEventListener('buffering', this._onBuffering);
    this.videoPlayer.addEventListener('loading', this._onLoading);

    const serverCertificate = IS_SAFARI && certificate ? await this._loadCertificate(certificate, headers) : undefined;

    this.videoPlayer.configure({
      textDisplayFactory: () => new CustomTextDisplay(this.parent),
      preferredAudioLanguage: this.parent.model.preferredAudio,
      preferredAudioChannelCount: this.parent.model.surroundSound ? 6 : 2,
      manifest: {
        retryParameters: {
          maxAttempts: 5,
        },
      },
      streaming: {
        alwaysStreamText: true,
        bufferingGoal: 30,
        ignoreTextStreamFailures: true,
        retryParameters: {
          maxAttempts: 5,
        },
      },
      drm: !license ? undefined : {
        servers: {
          'com.microsoft.playready': drm === 'playready' ? license : undefined,
          'com.widevine.alpha': drm === 'widevine' ? license : undefined,
          'com.apple.fps.1_0': drm === 'fairplay' ? license : undefined,
        },
        // It is recommended that a robustness level be specified.
        // Not specifying the robustness level could result in unexpected
        // behavior in the future, potentially including failure to play.
        advanced: {
          'com.widevine.alpha': {
            videoRobustness: 'SW_SECURE_DECODE',
            audioRobustness: 'SW_SECURE_CRYPTO',
          },
          'com.apple.fps.1_0': {
            serverCertificate,
          },
        },
        retryParameters: {
          maxAttempts: 5,
        },

        // eslint-disable-next-line no-unused-vars
        initDataTransform: (initData, initDataType, _drm) => {
          if (!IS_SAFARI) {
            return initData;
          }

          const contentId = extractContentIdFromInitData(initData);
          Logger.debug(`Extracted content id: ${contentId}`);

          return shaka.util.FairPlayUtils.initDataTransform(initData, contentId, serverCertificate);
        },
      },
    });

    this.videoPlayer.getNetworkingEngine()
      .registerRequestFilter((type, req) => {
        if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
          for (const [key, value] of Object.entries(headers)) {
            if (value) {
              req.headers[key] = value;
            }
          }

          req.headers.Authorization = `Bearer ${authorizationKey}`;
          req.headers['Magine-AccessToken'] = accessToken;
        }
      });

    // this is mostly for sd channels with mpeg dash and smooth streaming
    // par and sar you get in manifest can not be correct for sd streams where aspect ratio changes
    if (this.parent._aspectratioService) {
      this.videoPlayer.getNetworkingEngine()
        .registerResponseFilter((type, response) => {
          if (type === shaka.net.NetworkingEngine.RequestType.SEGMENT) {
            const sar = response.headers['x-amz-meta-sar'];
            if (sar) {
              const timeline = this.videoPlayer.getManifest().presentationTimeline;
              const offset = timeline.getSegmentAvailabilityStart() + timeline.getPresentationStartTime();

              this.parent._aspectratioService.addSAR(sar, offset);
            }
          }
        });
    }

    this.videoPlayer.getNetworkingEngine()
      .registerResponseFilter((type) => {
        if (type === shaka.net.NetworkingEngine.RequestType.MANIFEST) {
          this.parent.model = this.parent.model.merge({
            seekRange: this._getSeekRange(),
          });

          this.parent.dispatchEvent('manifestwillupdate');
        }
      });

    this.parent.dispatchEvent('playerisinitialized');
  }

  _disableAbr(disable) {
    const enabled = !disable && this.parent.model.selectedBitrate === 'auto';

    this.videoPlayer.configure({
      abr: {
        enabled,
      },
    });
  }

  async _loadCertificate(certificate, headers) {
    try {
      const data = await requestCertificate(certificate, headers);

      return new Uint8Array(data);
    } catch (e) {
      Logger.error('Failed to load certificate', e);

      throw e;
    }
  }

  _getPresentationStartTime() {
    if (!this.videoPlayer?.isLive()) {
      return 0;
    }

    if (IS_SAFARI) {
      return this._startOfLiveWindow;
    }

    if (!this._presentationStartTime) {
      // sometimes player returns a little bit different start time (1-4 sec)
      // that cause problems with DAI ads timing
      this._presentationStartTime = this.videoPlayer.getPresentationStartTimeAsDate()
        .getTime() / 1000;
    }

    return this._presentationStartTime;
  }

  _absoluteTimeToPlayerTime(absoluteTime) {
    return Math.round(absoluteTime - this._getPresentationStartTime());
  }

  playerTimeToAbsoluteTime(localTime) {
    return Math.round(localTime + this._getPresentationStartTime());
  }

  playerTimeToAbsoluteTimeFloat(localTime) {
    return localTime + this._getPresentationStartTime();
  }

  _getNormalizedBuffer() {
    const buffer = getNormalizedBuffer(this.parent.playerElement);

    return {
      start: this.playerTimeToAbsoluteTime(buffer.start),
      end: this.playerTimeToAbsoluteTime(buffer.end),
    };
  }

  _getSeekRange() {
    const seekRange = this.videoPlayer.seekRange();

    return {
      ...seekRange,
      startAbsolute: this.playerTimeToAbsoluteTime(seekRange.start),
      endAbsolute: this.playerTimeToAbsoluteTime(seekRange.end),
    };
  }

  _configureVideoPlayer() {
    this.parent.model = this.parent.model.merge({
      audioTracks: this.videoPlayer.getAudioLanguages(),
      videoTracks: this.parent.model.type !== 'hosted' ? QUALITIES : [QUALITIES[0]],
      textTracks: this._getAndPrepareTextTracks(),
      selectedText: SUBTITLES_OFF,
      isLive: this.videoPlayer.isLive() || this.videoPlayer.isInProgress(),
    });

    this.changeVolume(this.parent.model.volume, false);
    this.changeAudioPreference(this.parent.model.preferredAudio);
    this.changeTextTrackPreference(this.parent.model.preferredText);
    this.changeBitratePreference(this.parent.model.preferredBitrate);

    Logger.debug('Configuring video player...done');
  }

  _updateOffsetFromLive = () => {
    const {
      currentTime,
      isLive,
      offsetFromLive: previousOffsetFromLive,
    } = this.parent.model;

    const now = Date.now() / 1000;

    let offsetFromLive = 0;

    // currentTime is 0 when there is no data yet, so let's pretend there is no offset yet
    if (isLive && currentTime > 0) {
      offsetFromLive = Math.max(Math.round(now - currentTime), 0);
    }

    if (offsetFromLive === previousOffsetFromLive) {
      return;
    }

    this.parent.model = this.parent.model.merge({
      offsetFromLive,
    });

    this.parent.dispatchEvent('offsetFromLive', { offsetFromLive });
  };

  _updateModelWithBitrateAndCodecInformation(videoObject) {
    // Get estimated bitrate and use that for overall bitrate
    const bitrate = this.videoPlayer.getStats().estimatedBandwidth;

    this.parent.model = this.parent.model.merge({
      currentAdaptation: {
        videoCodec: videoObject.videoCodec,
        audioCodec: videoObject.audioCodec,
        videoBitrate: videoObject.videoBandwidth,
        audioBitrate: videoObject.audioBandwidth,
        bitrate,
      },
    });
  }

  _getActiveTextTrack() {
    const activeTrack = Array.from(this.parent.playerElement?.textTracks || [])
      .find(tt => tt.mode === 'showing');
    return activeTrack;
  }

  _getAndPrepareTextTracks() {
    const textTracks = filterForUniqueLanguages(this.videoPlayer.getTextTracks());

    if (IS_SAFARI) {
      if (textTracks.length === 1 && textTracks[0].language === 'und') return [];
      return normalizeTracks(textTracks);
    }

    // As shaka will return all text tracks available in the manifest we need
    // to filter out text tracks we don't support.
    const supportedCodec = 'stpp';
    const supportedMimeType = 'text/vtt';

    return textTracks
      .filter(track => track.codecs === supportedCodec || track.mimeType === supportedMimeType);
  }

  _getAndPrepareVideoTracks() {
    const variantTracks = this.videoPlayer.getVariantTracks();

    const activeTrack = variantTracks.find(track => track.active);

    if (activeTrack) {
      return variantTracks.filter(track => getLanguage(track.language) === getLanguage(activeTrack.language))
        .filter((variant, index, self) => self.findIndex(next => next.bandwidth === variant.bandwidth) === index)
        .sort(sortTracksByBandwidthAsc);
    }

    return variantTracks.slice(0)
      .sort(sortTracksByBandwidthAsc);
  }

  _onAirplayAvailabilityChange = (e) => {
    const isAirPlayAvailable = e.availability === 'available';

    this.model = this.model.merge({
      isAirPlayAvailable,
    });

    this.parent.dispatchEvent('isairplayavailable', { detail: isAirPlayAvailable });
  };

  _onAirplayDestinationChange = (e) => {
    const isAirPlaying = e.currentTarget.webkitCurrentPlaybackTargetIsWireless;

    this.model = this.model.merge({
      isAirPlaying,
    });

    this.parent.dispatchEvent('isairplaying', { detail: isAirPlaying });
  };

  _onTimeupdate = () => {
    if (!this.videoPlayer) return;

    const { viewable, defaultClosingCreditsOffset } = this.parent.model;
    const textTracks = this._getAndPrepareTextTracks();

    const currentTime = this.playerTimeToAbsoluteTime(this.parent.playerElement.currentTime);
    const currentTimeFloat = this.playerTimeToAbsoluteTimeFloat(this.parent.playerElement.currentTime);

    const duration = this.parent.model.mediaPresentationDuration
      || this.parent.playerElement.duration;
    const audioTracks = this.videoPlayer.getAudioLanguages();

    const buffer = this._getNormalizedBuffer();

    this.parent.model = this.parent.model.merge({
      buffer,
      currentTime,
      currentTimeFloat,
      duration,
      audioTracks,
      videoTracks: QUALITIES,
      textTracks,
      seekRange: this._getSeekRange(),
    });

    const streamType = getStreamType(viewable);
    if (streamType !== 'live') {
      // setup _closingCreditsStart if enabled
      if (defaultClosingCreditsOffset && !this._closingCreditsStart) {
        this._closingCreditsStart = viewable?.defaultPlayable?.closingCreditsStart
          || Math.max(
            duration - (defaultClosingCreditsOffset || 0),
            duration / 2, // min value is 50% of the duration
          );
      }

      if (
        this._closingCreditsStart // do not fire if not configured
        && currentTime >= this._closingCreditsStart
        && !this._creditsStartSent
      ) {
        this._creditsStartSent = true;
        this.parent.dispatchEvent('closingCreditsStart');
      }
      // restore event if scrolling
      if (currentTime < this._closingCreditsStart && this._creditsStartSent) {
        this._creditsStartSent = false;
      }
    }

    // do not dispatch 'ontimeupdate' while seeking
    if (this.parent.model.isSeeking) {
      return;
    }

    this.parent.dispatchEvent('ontimeupdate', {
      playerInfo: {
        buffer,
        currentTime,
        duration,
        audioTracks,
        videoTracks: QUALITIES,
        textTracks: textTracks.map(track => track.language),
        selectedQuality: this.model.selectedBitrate,
        selectedText: this.model.selectedText,
        selectedAudio: this.model.selectedAudio,
        isLive: this.parent.model.isLive,
      },
    });
  };

  _onEnded = () => {
    this.parent.model = this.parent.model.merge({
      isPlayedToEnd: true,
      isPlaying: false,
    });
    this.parent.dispatchEvent('streamEnded');
  };

  _onPlaying = () => {
    this.parent.model = this.parent.model.merge({
      isPlaying: true,
      isPlayedToEnd: false,
    });

    this._updateOffsetFromLive();
    this.parent.dispatchEvent('playing', {
      playerInfo: {
        currentTime: this.parent.model.currentTime,
        seekRange: this.parent.model.seekRange,
        isLive: this.parent.model.isLive,
        isPaused: false,
      },
    });
  };

  _onPause = () => {
    this.parent.model = this.parent.model.merge({
      isPlaying: false,
    });

    this.parent.dispatchEvent('pause', {
      playerInfo: {
        isPaused: true,
      },
    });
  };

  _onSeeking = () => {
    const {
      currentTime,
    } = this.parent.model;

    this.parent.model = this.parent.model.merge({
      isSeeking: true,
    });

    this.parent.dispatchEvent('seeking', {
      playerInfo: {
        currentTime,
      },
    });
  };

  _onSeeked = () => {
    const {
      currentTime,
    } = this.parent.model;

    if (this._applyFFWorkaround) {
      this.changeBitratePreference(this.parent.model.selectedBitrate);
      this._applyFFWorkaround = false;
    }

    this.parent.model = this.parent.model.merge({
      isSeeking: false,
    });

    this._updateOffsetFromLive();

    this.parent.dispatchEvent('seeked', {
      playerInfo: {
        currentTime,
      },
    });

    // notify about current time update
    this._onTimeupdate();
  };

  _onVolumeChange = () => {
    this.parent.dispatchEvent('volumechange', {});
  };

  _onLoadedmetadata = () => {
    const {
      assetId,
      protocol,
    } = this.parent.model;

    const isLive = this.videoPlayer.isLive() || this.videoPlayer.isInProgress();
    const streamType = isLive ? 'liveStream' : 'vod';
    const streamDuration = this.parent.playerElement.duration;

    // Update model
    this.parent.model = this.parent.model.merge({
      isLive,
      streamType,
      streamDuration,
    });

    // Dispatch data in event
    this.parent.dispatchEvent('playerInitialized', {
      playerInfo: {
        streamType,
        streamingProtocol: protocol,
        assetId,
      },
    });
  };

  _onAdaptation = () => {
    const { selectedBitrate } = this.parent.model;
    if (selectedBitrate && selectedBitrate !== 'auto') {
      this.changeBitratePreference(selectedBitrate);
      return;
    }

    let videoBandwidth = 0;
    let audioBandwidth = 0;
    let audioCodec = 'none';
    let videoCodec = 'none';

    const tracks = this.videoPlayer.getVariantTracks(); // all variant tracks

    tracks.forEach((t) => {
      if (t.active) { // an active track, that is, in use by the player right now
        videoBandwidth = t.videoBandwidth;
        audioBandwidth = t.audioBandwidth;
        audioCodec = t.audioCodec;
        videoCodec = t.videoCodec;
      }
    });

    const videoObject = {
      videoBandwidth,
      audioBandwidth,
      audioCodec,
      videoCodec,
    };
    this._updateModelWithBitrateAndCodecInformation(videoObject);
  };

  _onError = (e) => {
    this.parent._errorhandlingService?.onShakaError(new ShakaError(e.detail));
  };

  _onLoading = () => {
    // simulate initial buffering which sometimes isn't triggered
    this.parent.model = this.parent.model.merge({
      isBuffering: true,
      bufferingType: 'initial',
    });

    this.parent.dispatchEvent('buffering', {
      isBuffering: true,
    });
  };

  _onBuffering = (e) => {
    if (e.buffering) {
      const { isSeeking } = this.parent.model;
      let bufferingType = 'intermediate';
      if (isSeeking) {
        bufferingType = 'seek';
      }

      this.parent.model = this.parent.model.merge({
        isBuffering: true,
        bufferingType,
      });

      this.parent.dispatchEvent('buffering', {
        isBuffering: true,
      });
    } else {
      this.parent.model = this.parent.model.merge({
        isBuffering: false,
        bufferingType: 'initial',
      });

      this.parent.dispatchEvent('buffering', {
        isBuffering: false,
      });
    }
  };

  /**
   * Extract the beginning of the live window from the player. The player
   * parses the manifest and determines the period of time available for
   * live play.
   *
   * Dash player correspondent is _getPresentationStartTime()
   */
  _getStartOfLiveWindow() {
    const nowS = Date.now() / 1000;

    // Query the end of the first seekable time range. Assumes we only
    // have one timerange available
    const startOfSeekWindow = this.parent.playerElement.seekable.end(0);

    return nowS - startOfSeekWindow;
  }

  _setPlaybackStartTimeOnCanPlayEvent = () => {
    /* Since the canplay event triggers continously in a stream (when fastforward/rewind etc),
     * we should just set currentTime to playbackStartTime on the first one,
     * when we actually want to play from a specific location. */
    const {
      playbackStartTime,
    } = this.parent.model;

    if (this._playbackStarted) {
      return;
    }

    this._playbackStarted = true;
    this._startOfLiveWindow = this._getStartOfLiveWindow();

    if (IS_SAFARI && playbackStartTime) { // fixes playbackStartTime not taken into account on Safari
      this.seek(playbackStartTime);
    }

    // fire manifestWillUpdate event for hls protocol
    // this._startOfLiveWindow should be initialized if video is live
    this._manifestWillUpdate();
  };

  _manifestWillUpdate = () => {
    // fire manifestwillupdate event for browsers who support HLS
    if (this.parent.model.protocol === 'hls') {
      const fireEvent = () => {
        if (!this.videoPlayer) {
          // destroy in case no player (probably happens when shaka crashed)
          this.unload();
          return;
        }
        this.parent.model = this.parent.model.merge({
          seekRange: this._getSeekRange(),
        });
        this.parent.dispatchEvent('manifestwillupdate');
      };

      fireEvent();
      if (this.parent.model.isLive) {
        clearInterval(this._manifestInterval);
        this._manifestInterval = setInterval(fireEvent, MANIFEST_DELAY);
      }
    }
  };

  async unload() {
    clearInterval(this._liveOffsetInterval);
    clearInterval(this._manifestInterval);

    const selectedTextTrack = this._getActiveTextTrack();
    if (selectedTextTrack) {
      selectedTextTrack.removeEventListener('cuechange', this._onCueChange);
    }

    if (this.videoPlayer?.destroy) {
      this.videoPlayer.setTextTrackVisibility(false);
      this.videoPlayer.removeEventListener('error', this._onError);
      this.videoPlayer.removeEventListener('adaptation', this._onAdaptation);
      this.videoPlayer.removeEventListener('buffering', this._onBuffering);
      this.videoPlayer.removeEventListener('loading', this._onLoading);

      if (window.WebKitPlaybackTargetAvailabilityEvent) {
        this.parent.playerElement.removeEventListener(
          'webkitplaybacktargetavailabilitychanged',
          this._onAirplayAvailabilityChange,
        );
        this.parent.playerElement.removeEventListener(
          'webkitcurrentplaybacktargetiswirelesschanged',
          this._onAirplayDestinationChange,
        );
      }
      this.parent.playerElement.removeEventListener('canplay', this._setPlaybackStartTimeOnCanPlayEvent);
      this.parent.playerElement.removeEventListener('timeupdate', this._onTimeupdate);
      this.parent.playerElement.removeEventListener('ended', this._onEnded);
      this.parent.playerElement.removeEventListener('playing', this._onPlaying);
      this.parent.playerElement.removeEventListener('pause', this._onPause);
      this.parent.playerElement.removeEventListener('seeking', this._onSeeking);
      this.parent.playerElement.removeEventListener('seeked', this._onSeeked);
      this.parent.playerElement.removeEventListener('volumechange', this._onVolumeChange);
      this.parent.playerElement.removeEventListener('loadedmetadata', this._onLoadedmetadata);
      this.parent.removeEventListener('adsActivated', this._hideSubtitles);
      this.parent.removeEventListener('adsDeactivated', this._showSubtitles);

      if (IS_SAFARI) {
        this._safariSubtitlesManager?.unload();
      }

      const destroyPromise = this.videoPlayer.destroy();
      this.videoPlayer.mux?.destroy();
      await destroyPromise; // do not wait before mux.destroy - errors
      this.videoPlayer = null;
    }
  }

  destroy() {
    this.unload();
    this._safariSubtitlesManager?.destroy();
    delete this._safariSubtitlesManager;
  }
}
